import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { FaInstagram, FaFacebookF, FaBars, FaTimes } from 'react-icons/fa';

function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Shift+A to open admin
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        window.location.href = '/admin';
      }
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      // Auto-close mobile menu when resizing to desktop
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Mobile Header */}
      {isMobile && (
        <div className="mobile-header">
          <div className="mobile-logo">
            <Link to="/" onClick={closeMobileMenu}>
              <img 
                src="https://i.postimg.cc/mr8hM7z1/PNG_Logo.png" 
                alt="CineHouseStudio"
                style={{ maxWidth: '150px', maxHeight: '50px' }}
              />
            </Link>
          </div>
          <button 
            className="hamburger-button" 
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      )}

      {/* Sidebar - transforms for mobile */}
      <aside className={`sidebar ${isMobile ? 'mobile-sidebar' : ''} ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="logo">
          <Link to="/" onClick={closeMobileMenu}>
            <img 
              src="https://i.postimg.cc/mr8hM7z1/PNG_Logo.png" 
              alt="CineHouseStudio"
              style={{ maxWidth: '221px', maxHeight: '95px' }}
            />
          </Link>
        </div>

        {/* Close button for mobile */}
        {isMobile && isMobileMenuOpen && (
          <button 
            className="mobile-close-btn"
            onClick={closeMobileMenu}
            aria-label="Close menu"
          >
            <FaTimes />
          </button>
        )}

        <ul className="menu-list">
          <li className="menu-item">
            <NavLink 
              to="/" 
              className={({ isActive }) => isActive ? 'active' : ''}
              onClick={closeMobileMenu}
            >
              Main
            </NavLink>
          </li>
          <li className="menu-item">
            <NavLink 
              to="/about"  // Add About link
              className={({ isActive }) => isActive ? 'active' : ''}
              onClick={closeMobileMenu}
            >
              About
            </NavLink>
          </li>
          <li className="menu-item">
            <NavLink 
              to="/portfolio" 
              className={({ isActive }) => isActive ? 'active' : ''}
              onClick={closeMobileMenu}
            >
              Portfolio
            </NavLink>
          </li>
          <li className="menu-item">
            <NavLink 
              to="/cinematic" 
              className={({ isActive }) => isActive ? 'active' : ''}
              onClick={closeMobileMenu}
            >
              Cinematic
            </NavLink>
          </li>
          <li className="menu-item">
            <NavLink 
              to="/reviews" 
              className={({ isActive }) => isActive ? 'active' : ''}
              onClick={closeMobileMenu}
            >
              Clients Love
            </NavLink>
          </li>
          <li className="menu-item">
            <NavLink 
              to="/contact" 
              className={({ isActive }) => isActive ? 'active' : ''}
              onClick={closeMobileMenu}
            >
              Contact
            </NavLink>
          </li>
        </ul>

        <footer className="sidebar-footer">
          <div className="social-links">
            <a 
              href="https://www.instagram.com/cinehousestudio?igsh=MTY2Y2kxZXo0eDBhdg%3D%3D" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="item"
              onClick={closeMobileMenu}
            >
              <FaInstagram />
            </a>
            <a 
              href="https://share.google/DxGnyxNf996nCwiGM" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="item"
              onClick={closeMobileMenu}
            >
              <FaFacebookF />
            </a>
          </div>
          <div className="footer-content">
            <p>Grace in every frame, love in every story. © 2025 CineHouseStudio. With gratitude.</p>
          </div>
          <div className="branding">
            Site by <a href="https://purvakj.github.io/purvak/" target="_blank" rel="noopener noreferrer">SSIT</a>
          </div>
        </footer>
      </aside>

      {/* Overlay for mobile menu */}
      {isMobile && isMobileMenuOpen && (
        <div className="mobile-overlay" onClick={closeMobileMenu} />
      )}

      <style jsx>{`
        .mobile-header {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 70px;
          background: white;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          z-index: 1000;
          padding: 0 20px;
          align-items: center;
          justify-content: space-between;
        }

        .hamburger-button {
          background: none;
          border: none;
          font-size: 24px;
          color: #333;
          cursor: pointer;
          padding: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.3s ease;
        }

        .hamburger-button:hover {
          color: #007bff;
        }

        .mobile-close-btn {
          position: absolute;
          top: 20px;
          right: 20px;
          background: none;
          border: none;
          font-size: 24px;
          color: #333;
          cursor: pointer;
          padding: 10px;
          z-index: 1002;
          display: none;
          color: white;
        }

        .mobile-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 998;
          backdrop-filter: blur(3px);
        }

        /* Active link styles - updated to brown */
        .menu-item a.active {
          color: var(--color-link, #aa8d74) !important;
          text-decoration: underline;
        }

        .menu-item a:hover {
          color: var(--color-link, #aa8d74);
        }

        @media (max-width: 768px) {
          .mobile-header {
            display: flex;
          }

          .sidebar.mobile-sidebar {
            position: fixed;
            top: 30px;
            left: -100%;
            width: 280px;
            height: 100vh;
            background: white;
            z-index: 999;
            transition: left 0.3s ease;
            box-shadow: 2px 0 10px rgba(0,0,0,0.1);
            overflow-y: auto;
            padding-top: 80px;
          }

          .sidebar.mobile-sidebar.open {
            left: 0;
          }

          .sidebar.mobile-sidebar .logo {
            padding: 20px;
            margin-top: -60px;
          }

          .sidebar.mobile-sidebar .logo img {
            max-width: 180px;
          }

          .mobile-close-btn {
            display: block;
          }

          .sidebar.mobile-sidebar .menu-list {
            padding: 20px;
          }

          .sidebar.mobile-sidebar .menu-item {
            margin-bottom: 15px;
          }

          .sidebar.mobile-sidebar .menu-item a {
            font-size: 18px;
            padding: 10px 15px;
            display: block;
            border-radius: 8px;
            transition: all 0.3s ease;
          }

          .sidebar.mobile-sidebar .menu-item a:hover,
          .sidebar.mobile-sidebar .menu-item a.active {
            background: #f8f9fa;
            color: var(--color-link, #aa8d74) !important;
          }

          .sidebar.mobile-sidebar .sidebar-footer {
            padding: 20px;
            text-align: center;
          }

          .sidebar.mobile-sidebar .social-links {
            justify-content: center;
            margin-bottom: 15px;
          }

          .sidebar.mobile-sidebar .social-links .item {
            margin: 0 10px;
            font-size: 20px;
          }

          .sidebar.mobile-sidebar .footer-content p {
            font-size: 12px;
          }

          .sidebar.mobile-sidebar .branding {
            font-size: 12px;
          }

          /* Hide desktop sidebar by default on mobile */
          .sidebar:not(.mobile-sidebar) {
            display: none;
          }
        }
      `}</style>
    </>
  );
}

export default Navbar;